"use client";

import React from "react";
import Link from "next/link";
import { 
  Utensils, 
  Wine, 
  Sparkles, 
  Award, 
  Clock, 
  KanbanSquare, 
  ArrowRight,
  ShieldCheck,
  Star
} from "lucide-react";

interface RestaurantHeroProps {
  onOpenReservation: () => void;
  onOpenPrivateDining: () => void;
  currency: "INR" | "USD";
}

export default function RestaurantHero({
  onOpenReservation,
  onOpenPrivateDining,
  currency,
}: RestaurantHeroProps) {
  return (
    <div className="relative overflow-hidden bg-stone-950 text-stone-100 pt-10 pb-16 border-b border-stone-800">
      {/* Ambient Lighting & Glow Orbs */}
      <div className="absolute top-0 left-1/3 w-[500px] h-[500px] bg-amber-600/10 rounded-full blur-[120px] pointer-events-none" />
      <div className="absolute top-1/2 right-10 w-96 h-96 bg-red-950/20 rounded-full blur-[100px] pointer-events-none" />

      <div className="max-w-7xl mx-auto px-4 sm:px-6 relative z-10">
        {/* Top Badges & Quick CRM Link */}
        <div className="flex flex-wrap items-center justify-between gap-4 mb-6">
          <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-amber-500/10 border border-amber-500/30 text-amber-300 text-xs font-bold tracking-wider uppercase shadow-inner">
            <Sparkles className="w-3.5 h-3.5 text-amber-400" />
            <span>L'AURA • Modern Gastronomy & Wine Cellar</span>
          </div>

          <div className="flex items-center gap-3">
            <Link
              href="/demos/restaurant/crm"
              className="px-3.5 py-1.5 rounded-lg bg-stone-900 hover:bg-stone-800 border border-amber-500/30 text-amber-400 text-xs font-bold flex items-center gap-1.5 transition-all shadow-sm group"
            >
              <KanbanSquare className="w-3.5 h-3.5" />
              <span>Explore Restaurant Floor & Sales CRM</span>
              <span className="text-[10px] text-stone-400 group-hover:text-white transition-colors">→</span>
            </Link>
          </div>
        </div>

        {/* Hero Main Content */}
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-10 items-center">
          {/* Left Column: Headlines & CTAs */}
          <div className="lg:col-span-7 space-y-6">
            <div className="flex items-center gap-2 text-xs font-semibold text-amber-400">
              <div className="flex items-center text-amber-400">
                {[...Array(5)].map((_, i) => (
                  <Star key={i} className="w-3.5 h-3.5 fill-amber-400 text-amber-400" />
                ))}
              </div>
              <span className="text-stone-300">Michelin Guide Selected 2026 • Wine Spectator Grand Award</span>
            </div>

            <h1 className="text-3xl sm:text-4xl md:text-5xl font-extrabold tracking-tight text-white leading-[1.15]">
              Modern Gastronomy Meets <br />
              <span className="bg-gradient-to-r from-amber-400 via-amber-200 to-amber-500 bg-clip-text text-transparent">
                Rare Vintage Cellar Flights
              </span>
            </h1>

            <p className="text-base sm:text-lg text-stone-300 max-w-2xl leading-relaxed">
              Immerse yourself in progressive multi-course tasting menus, bespoke subterranean cellar salons, and front-row culinary theater curated by Executive Chef Jean-Luc & Head Sommelier Aarav Kapoor.
            </p>

            {/* Quick CTAs */}
            <div className="flex flex-wrap items-center gap-3.5 pt-2">
              <button
                onClick={onOpenReservation}
                className="px-6 py-3 rounded-xl bg-gradient-to-r from-amber-600 via-amber-500 to-amber-600 hover:from-amber-500 hover:to-amber-500 text-white font-bold text-sm shadow-xl shadow-amber-600/30 transition-all hover:scale-[1.02] flex items-center gap-2"
              >
                <Utensils className="w-4 h-4" />
                <span>Reserve a Table Tonight</span>
              </button>

              <button
                onClick={onOpenPrivateDining}
                className="px-5 py-3 rounded-xl bg-stone-900 hover:bg-stone-800 text-stone-200 font-semibold text-sm border border-stone-700 hover:border-amber-500/50 transition-all flex items-center gap-2 shadow-sm"
              >
                <Wine className="w-4 h-4 text-amber-400" />
                <span>Private Salons & VIP Banquets</span>
              </button>
            </div>

            {/* Micro Highlights */}
            <div className="pt-6 border-t border-stone-800/80 grid grid-cols-3 gap-4 text-left">
              <div>
                <span className="block text-xl sm:text-2xl font-bold text-white">7 to 9</span>
                <span className="text-xs text-stone-400">Degustation Courses</span>
              </div>
              <div>
                <span className="block text-xl sm:text-2xl font-bold text-amber-400">2,400+</span>
                <span className="text-xs text-stone-400">Cellar Wine Vintages</span>
              </div>
              <div>
                <span className="block text-xl sm:text-2xl font-bold text-white">3 Salons</span>
                <span className="text-xs text-stone-400">Private Dining Vaults</span>
              </div>
            </div>
          </div>

          {/* Right Column: Hero Visual Showcase */}
          <div className="lg:col-span-5 relative">
            <div className="relative rounded-2xl overflow-hidden border border-amber-500/20 shadow-2xl group bg-stone-900">
              <img
                src="https://images.unsplash.com/photo-1544025162-d76694265947?auto=format&fit=crop&w=1000&q=80"
                alt="L'Aura Fine Dining"
                className="w-full h-80 object-cover object-center group-hover:scale-105 transition-transform duration-700 brightness-90"
              />
              <div className="absolute inset-0 bg-gradient-to-t from-stone-950 via-stone-950/40 to-transparent" />

              <div className="absolute bottom-0 inset-x-0 p-5 space-y-2">
                <div className="flex items-center justify-between">
                  <span className="text-xs font-bold text-amber-400 uppercase tracking-wider bg-black/60 px-2.5 py-0.5 rounded backdrop-blur-md border border-amber-500/30">
                    Tonight's Sitting: 07:15 PM & 08:30 PM
                  </span>
                  <span className="text-xs text-emerald-400 font-semibold flex items-center gap-1">
                    <span className="w-2 h-2 rounded-full bg-emerald-400 animate-ping" />
                    4 Tables Open
                  </span>
                </div>

                <h4 className="text-lg font-bold text-white">The Sommelier's Wine Vault & Chef's Counter</h4>
                <p className="text-xs text-stone-300 line-clamp-2">
                  Dry-aged Kagoshima Wagyu, Perigord winter truffles, and Grand Cru vintage flights decanted tableside.
                </p>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
